// src/pages/TutorialPage.js - Tutorial page with step-by-step guide
import React, { useState } from 'react';
import styled from 'styled-components';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import EmailSimulator from '../components/interactive/EmailSimulator';

const PageTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: var(--spacing-lg);
`;

const TutorialContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 3fr;
  gap: var(--spacing-xl);
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const Sidebar = styled.div`
  position: sticky;
  top: 100px;
  height: fit-content;
`;

const SidebarNav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;

const NavItem = styled.button`
  display: flex;
  align-items: center;
  padding: var(--spacing-md);
  text-align: left;
  background-color: ${props => props.active ? 'var(--primary-color)' : 'var(--background-light)'};
  color: ${props => props.active ? 'white' : 'var(--text-color)'};
  border-radius: var(--border-radius);
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all var(--transition-speed) ease;
  
  &:hover {
    background-color: ${props => props.active ? 'var(--primary-dark)' : 'var(--background-dark)'};
  }
  
  span {
    margin-right: var(--spacing-md);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background-color: ${props => props.active ? 'white' : 'var(--primary-color)'};
    color: ${props => props.active ? 'var(--primary-color)' : 'white'};
    font-size: 0.875rem;
    font-weight: bold;
  }
`;

const NavStatus = styled.div`
  margin-left: auto;
  font-size: 0.75rem;
  background-color: ${props => props.completed ? 'var(--success-color)' : 'var(--text-light)'};
  color: white;
  padding: 2px 8px;
  border-radius: 10px;
`;

const TutorialContent = styled.div`
  background-color: var(--background-color);
  border-radius: var(--border-radius);
  padding: var(--spacing-xl);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const ContentHeader = styled.div`
  margin-bottom: var(--spacing-xl);
  padding-bottom: var(--spacing-md);
  border-bottom: 1px solid var(--background-light);
`;

const SectionTitle = styled.h2`
  font-size: 1.75rem;
  margin-bottom: var(--spacing-sm);
`;

const SectionDescription = styled.p`
  color: var(--text-light);
  font-size: 1.125rem;
`;

const ContentBody = styled.div`
  margin-bottom: var(--spacing-xl);
`;

const Step = styled.div`
  margin-bottom: var(--spacing-xl);
`;

const StepTitle = styled.h3`
  font-size: 1.25rem;
  margin-bottom: var(--spacing-md);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  
  &::before {
    content: '${props => props.number}';
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background-color: var(--primary-color);
    color: white;
    font-size: 0.875rem;
    font-weight: bold;
  }
`;

const NavigationButtons = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: var(--spacing-xxl);
`;

const tutorials = [
  {
    id: 'introduction',
    title: 'Introduction to PHI',
    steps: [
      {
        title: 'What is Protected Health Information (PHI)?',
        content: 'Protected Health Information (PHI) refers to any identifiable health information that is used, disclosed, stored, or transmitted by a HIPAA-covered entity or its business associate. This includes a wide range of identifiers, from names and addresses to medical record numbers and biometric data.',
      },
      {
        title: 'Why is proper PHI handling important?',
        content: 'Improper handling of PHI can lead to data breaches, financial penalties, damage to organizational reputation, and most importantly, harm to patients. Understanding how to correctly handle PHI via email is essential for compliance and maintaining patient trust.',
      },
      {
        title: 'Legal requirements and regulations',
        content: 'The Health Insurance Portability and Accountability Act (HIPAA) establishes national standards for the protection of certain health information. The HIPAA Privacy Rule and Security Rule provide specific guidelines on how PHI should be handled, including via electronic communications like email.',
      }
    ]
  },
  {
    id: 'email-security',
    title: 'Email Security Basics',
    steps: [
      {
        title: 'Email security risks',
        content: 'Email is inherently insecure and susceptible to various risks such as interception, mis-delivery, forwarding, and unauthorized access. Understanding these risks is the first step in proper PHI handling.',
      },
      {
        title: 'When to include PHI in emails',
        content: 'PHI should only be included in emails when absolutely necessary for patient care or operational purposes. Always consider if the same goal can be achieved without including PHI.',
      },
      {
        title: 'General email security best practices',
        content: 'Always verify recipient email addresses before sending, use clear and specific subject lines (without PHI), and regularly update your passwords. Never access work email on unsecured public networks.',
      }
    ]
  },
  {
    id: 'encryption-method-1',
    title: 'Encryption Method',
    steps: [
      {
        title: 'Using Outlook encryption features',
        content: 'Microsoft Outlook provides built-in encryption options to secure email communications containing PHI.',
        interactive: 'email-encryption-demo',
      },
      {
        title: 'Step-by-step guide',
        content: 'Follow these steps to encrypt an email in Outlook:',
        list: [
          'Compose a new email message',
          'In the top ribbon, click on "Options"',
          'Look for the "Permission" button and select "Encrypt"',
          'Choose the appropriate encryption option',
          'Compose your message and send as normal'
        ]
      },
      {
        title: 'Verifying encryption',
        content: 'To verify your email has been encrypted, look for the encryption icon in the sent items folder. Recipients will see a notification that they\'ve received an encrypted message.',
      }
    ]
  },
  {
    id: 'best-practices',
    title: 'Mandatory Requirements',
    steps: [
      {
        title: 'Minimizing PHI in email',
        content: 'Even with encryption, you MUST minimize the amount of PHI included in emails. Use patient identifiers instead of full names when possible, and never include PHI in email subject lines.',
      },
      {
        title: 'Handling email replies and forwards',
        content: 'When replying to or forwarding emails containing PHI, you MUST ensure all recipients have a legitimate need to know and that encryption remains enabled throughout the email chain.',
      },
      {
        title: 'What to do if PHI is sent unsecurely',
        content: 'If you discover PHI has been sent unsecurely, you MUST immediately report the incident to your supervisor and privacy officer. Do not try to recall the email as this can draw more attention to the breach.',
      }
    ]
  }
];

const TutorialPage = () => {
  const [activeTutorial, setActiveTutorial] = useState(tutorials[0]);
  const [completedTutorials, setCompletedTutorials] = useState({});

  const handleTutorialClick = (tutorial) => {
    setActiveTutorial(tutorial);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleComplete = () => {
    setCompletedTutorials({
      ...completedTutorials,
      [activeTutorial.id]: true
    });
  };

  const currentIndex = tutorials.findIndex(t => t.id === activeTutorial.id);
  const previousTutorial = tutorials[currentIndex - 1];
  const nextTutorial = tutorials[currentIndex + 1];

  return (
    <>
      <PageTitle>Email PHI Handling Tutorial</PageTitle>
      
      <TutorialContainer>
        <Sidebar>
          <Card>
            <SidebarNav>
              {tutorials.map((tutorial, index) => (
                <NavItem 
                  key={tutorial.id}
                  active={activeTutorial.id === tutorial.id}
                  onClick={() => handleTutorialClick(tutorial)}
                >
                  <span>{index + 1}</span>
                  {tutorial.title}
                  {completedTutorials[tutorial.id] && (
                    <NavStatus completed>Completed</NavStatus>
                  )}
                </NavItem>
              ))}
            </SidebarNav>
          </Card>
        </Sidebar>
        
        <TutorialContent>
          <ContentHeader>
            <SectionTitle>{activeTutorial.title}</SectionTitle>
            <SectionDescription>
              {activeTutorial.id === 'introduction' && 'Learn the basics of PHI and why proper handling is crucial.'}
              {activeTutorial.id === 'email-security' && 'Understand email security risks and basic principles.'}
              {activeTutorial.id === 'encryption-method-1' && 'Learn how to use Outlook encryption features to secure emails containing PHI.'}
              {activeTutorial.id === 'best-practices' && 'Critical requirements that all employees MUST follow when handling PHI in emails.'}
            </SectionDescription>
          </ContentHeader>
          
          <ContentBody>
            {activeTutorial.steps.map((step, index) => (
              <Step key={index}>
                <StepTitle number={index + 1}>{step.title}</StepTitle>
                <p>{step.content}</p>
                
                {step.list && (
                  <ul style={{ marginTop: 'var(--spacing-md)', marginLeft: 'var(--spacing-xl)' }}>
                    {step.list.map((item, i) => (
                      <li key={i} style={{ marginBottom: 'var(--spacing-sm)' }}>{item}</li>
                    ))}
                  </ul>
                )}
                
                {step.interactive === 'email-encryption-demo' && (
                  <div style={{ marginTop: 'var(--spacing-lg)' }}>
                    <EmailSimulator />
                  </div>
                )}
              </Step>
            ))}
          </ContentBody>
          
          <Button onClick={handleComplete}>
            Mark as Completed
          </Button>
          
          <NavigationButtons>
            {previousTutorial && (
              <Button 
                variant="secondary" 
                onClick={() => handleTutorialClick(previousTutorial)}
              >
                ← Previous: {previousTutorial.title}
              </Button>
            )}
            
            <div style={{ flexGrow: 1 }} />
            
            {nextTutorial && (
              <Button 
                onClick={() => handleTutorialClick(nextTutorial)}
              >
                Next: {nextTutorial.title} →
              </Button>
            )}
          </NavigationButtons>
        </TutorialContent>
      </TutorialContainer>
    </>
  );
};

export default TutorialPage; 